import React, {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useDropzone} from 'react-dropzone';
import {doc, getDoc, updateDoc} from 'firebase/firestore';
import {deleteObject, getDownloadURL, ref, uploadBytes} from 'firebase/storage';
import {db, storage} from '../../firebase.config';
import AdminNavbar from "./AdminNavbar";

const PROJECT_TYPES = ['conversion', 'extension', 'new build', 'renovation', 'landscape'];

function UpdateProject() {
    const {projectId} = useParams();

    const [header, setHeader] = useState('');
    const [existingImages, setExistingImages] = useState([]);
    const [newFiles, setNewFiles] = useState([]);
    const [description, setDescription] = useState('');
    const [projectType, setProjectType] = useState('');

    const [mainImageId, setMainImageId] = useState(null);

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [imagesToDelete, setImagesToDelete] = useState([]);

    useEffect(function() {
        async function fetchProject() {
            try {
                const docRef = doc(db, 'projects', projectId);
                const docSnap = await getDoc(docRef);

                if (!docSnap.exists()) {
                    setError('Project not found');
                    setLoading(false);
                    return;
                }

                const data = docSnap.data();
                setHeader(data.header || '');
                const sortedImages = (data.images || []).sort(function(a, b) {
                    return a.ordinal - b.ordinal;
                });
                setExistingImages(sortedImages);

                const mainIndex = sortedImages.findIndex(function(img) {
                    return img.mainImage === true;
                });
                if (mainIndex !== -1) {
                    setMainImageId('existing-' + mainIndex);
                }

                setDescription(data.description || '');
                setProjectType(data.projectType || '');
                setLoading(false);
            } catch (err) {
                console.error('Error fetching project:', err);
                setError('Failed to load project');
                setLoading(false);
            }
        }

        fetchProject();
    }, [projectId]);

    function validateForm() {
        if (!header.trim()) {
            setError('Header is required');
            return false;
        }

        const totalImages = existingImages.length + newFiles.length;
        if (totalImages === 0) {
            setError('At least one image is required');
            return false;
        }
        if (totalImages > 5) {
            setError('A maximum of 5 images are allowed');
            return false;
        }

        if (!projectType) {
            setError('Project type is required');
            return false;
        }

        if (!PROJECT_TYPES.includes(projectType)) {
            setError('Invalid project type');
            return false;
        }

        return true;
    }

    const onDrop = useCallback(function(acceptedFiles) {
        const filesWithPreview = acceptedFiles.map(function(file) {
            return Object.assign(file, {
                preview: URL.createObjectURL(file)
            });
        });
        setNewFiles(function(prev) {
            return [...prev, ...filesWithPreview];
        });
    }, []);

    const {getRootProps, getInputProps, isDragActive} = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
        },
        maxSize: 12 * 1024 * 1024
    });

    function handleRemoveExistingImage(imageToRemove, indexToRemove) {
        setExistingImages(function(prev) {
            return prev.filter(function(img) {
                return img.url !== imageToRemove.url;
            });
        });
        setImagesToDelete(function(prev) {
            return [...prev, imageToRemove.url];
        });

        const removedId = 'existing-' + indexToRemove;
        if (mainImageId === removedId) {
            setMainImageId(null);
        } else if (mainImageId && mainImageId.startsWith('existing-')) {
            const currentIndex = parseInt(mainImageId.split('-')[1]);
            if (currentIndex > indexToRemove) {
                setMainImageId('existing-' + (currentIndex - 1));
            }
        }
    }

    function handleRemoveNewFile(index) {
        setNewFiles(function(prev) {
            const updated = [...prev];
            URL.revokeObjectURL(updated[index].preview);
            updated.splice(index, 1);
            return updated;
        });

        const removedId = 'new-' + index;
        if (mainImageId === removedId) {
            setMainImageId(null);
        } else if (mainImageId && mainImageId.startsWith('new-')) {
            const currentIndex = parseInt(mainImageId.split('-')[1]);
            if (currentIndex > index) {
                setMainImageId('new-' + (currentIndex - 1));
            }
        }
    }

    async function uploadNewImages(startOrdinal) {
        return await Promise.all(
            newFiles.map(async function(file, index) {
                const storageRef = ref(storage, `projects/${Date.now()}_${file.name}`);
                await uploadBytes(storageRef, file);
                const url = await getDownloadURL(storageRef);
                return {
                    url,
                    ordinal: startOrdinal + index,
                    mainImage: mainImageId === 'new-' + index
                };
            })
        );
    }

    async function deleteRemovedImages() {
        await Promise.all(
            imagesToDelete.map(async function(url) {
                try {
                    const imageRef = ref(storage, url);
                    await deleteObject(imageRef);
                } catch (err) {
                    console.warn('Failed to delete image from storage:', err);
                }
            })
        );
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!validateForm()) return;

        setSubmitting(true);
        try {
            const reindexedExisting = existingImages.map(function(img, index) {
                return {
                    url: img.url,
                    ordinal: index,
                    mainImage: mainImageId === 'existing-' + index
                };
            });

            const uploadedNew = await uploadNewImages(reindexedExisting.length);

            const allImages = [...reindexedExisting, ...uploadedNew];

            const docRef = doc(db, 'projects', projectId);
            await updateDoc(docRef, {
                header: header.trim(),
                images: allImages,
                description: description.trim() || null,
                projectType
            });

            await deleteRemovedImages();

            newFiles.forEach(function(file) {
                URL.revokeObjectURL(file.preview);
            });

            setSuccess('Project updated successfully');
            setNewFiles([]);
            setImagesToDelete([]);
        } catch (err) {
            console.error('Error updating project:', err);
            setError('Failed to update project. Please try again.');
        } finally {
            setSubmitting(false);
        }
    }

    useEffect(function() {
        return function() {
            newFiles.forEach(function(file) {
                URL.revokeObjectURL(file.preview);
            });
        };
    }, [newFiles]);

    function handleHeaderChange(e) {
        setHeader(e.target.value);
    }

    function handleDescriptionChange(e) {
        setDescription(e.target.value);
    }

    function handleProjectTypeChange(e) {
        setProjectType(e.target.value);
    }

    if (loading) {
        return (
            <div className="bg-gray-50">
                <AdminNavbar/>
                <div className="container min-h-screen w-screen relative z-30 mx-auto px-4 mt-[110px] py-16 pb-36">
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#081542]"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50">
            <AdminNavbar/>
            <div
              className={"min-h-screen w-screen relative z-30 mx-auto px-4 mt-[110px] py-16 pb-36 flex justify-center"}>
                <div className="container">
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-[28px] font-semibold text-[#081542]">Update Project</h1>
                    </div>

                    <div className="bg-white p-6 rounded shadow">
                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2" htmlFor="header">
                                    Project Header *
                                </label>
                                <input
                                  type="text"
                                  id="header"
                                  value={header}
                                  onChange={handleHeaderChange}
                                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
                                  placeholder="Enter project header"
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2">
                                    Project Images *
                                </label>

                                {existingImages.length > 0 && (
                                  <div className="mb-4">
                                      <p className="text-sm text-gray-500 mb-2">
                                          Existing Images - Click the circle to set an image as the main image.
                                      </p>
                                      <div className="flex flex-wrap gap-4">
                                          {existingImages.map(function (image, index) {
                                              const imageId = 'existing-' + index;
                                              const isMain = mainImageId === imageId;
                                              return (
                                                <div key={image.url} className="relative p-2">
                                                    <img
                                                      src={image.url}
                                                      alt={`Existing ${index + 1}`}
                                                      className="h-36 aspect-video object-cover rounded"
                                                    />
                                                    <button
                                                      type="button"
                                                      onClick={function () {
                                                          setMainImageId(isMain ? null : imageId);
                                                      }}
                                                      className={`absolute top-4 left-4 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors
                                                            ${isMain
                                                        ? 'bg-[#081542] border-[#081542]'
                                                        : 'bg-white border-gray-300 hover:border-[#081542]'}`}
                                                    >
                                                        {isMain && (
                                                          <svg className="w-4 h-4 text-white" fill="currentColor"
                                                               viewBox="0 0 20 20">
                                                              <path fillRule="evenodd"
                                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                    clipRule="evenodd"/>
                                                          </svg>
                                                        )}
                                                    </button>
                                                    {isMain && (
                                                      <span
                                                        className="absolute bottom-4 left-4 bg-[#081542] text-white text-xs px-2 py-1 rounded">
                                                            Main
                                                        </span>
                                                    )}
                                                    <button
                                                      type="button"
                                                      onClick={function () {
                                                          handleRemoveExistingImage(image, index);
                                                      }}
                                                      className="absolute -top-0 -right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                                                    >
                                                        x
                                                    </button>
                                                    <span
                                                      className="absolute bottom-1 right-1 bg-black bg-opacity-80 text-white text-base px-2 rounded">
                                                        {index + 1}
                                                    </span>
                                                </div>
                                              );
                                          })}
                                      </div>
                                  </div>
                                )}

                                <div
                                  {...getRootProps()}
                                  className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors
                                    ${isDragActive
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-300 hover:border-gray-400'
                                  }`}
                                >
                                    <input {...getInputProps()} />
                                    {isDragActive ? (
                                      <p className="text-blue-500">Drop the images here...</p>
                                    ) : (
                                      <div>
                                          <p className="text-gray-600">Drag & drop images here, or click to select</p>
                                          <p className="text-gray-400 text-sm mt-1">Max 12MB per image</p>
                                      </div>
                                    )}
                                </div>

                                {newFiles.length > 0 && (
                                  <div className="mt-4">
                                      <p className="text-sm text-gray-500 mb-2">New Images</p>
                                      <div className="flex flex-wrap gap-4 pt-2">
                                          {newFiles.map(function (file, index) {
                                              const imageId = 'new-' + index;
                                              const isMain = mainImageId === imageId;
                                              return (
                                                <div key={index} className="relative p-2">
                                                    <img
                                                      src={file.preview}
                                                      alt={`New ${index + 1}`}
                                                      className="h-36 aspect-video object-cover rounded"
                                                    />
                                                    <button
                                                      type="button"
                                                      onClick={function () {
                                                          setMainImageId(isMain ? null : imageId);
                                                      }}
                                                      className={`absolute top-4 left-4 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors
                                                            ${isMain
                                                        ? 'bg-[#081542] border-[#081542]'
                                                        : 'bg-white border-gray-300 hover:border-[#081542]'}`}
                                                    >
                                                        {isMain && (
                                                          <svg className="w-4 h-4 text-white" fill="currentColor"
                                                               viewBox="0 0 20 20">
                                                              <path fillRule="evenodd"
                                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                    clipRule="evenodd"/>
                                                          </svg>
                                                        )}
                                                    </button>
                                                    {isMain && (
                                                      <span
                                                        className="absolute bottom-4 left-4 bg-[#081542] text-white text-xs px-2 py-1 rounded">
                                                            Main
                                                        </span>
                                                    )}
                                                    <button
                                                      type="button"
                                                      onClick={function () {
                                                          handleRemoveNewFile(index);
                                                      }}
                                                      className="absolute -top-0 -right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                                                    >
                                                        x
                                                    </button>
                                                    <span
                                                      className="absolute bottom-1 right-1 bg-black bg-opacity-80 text-white text-base px-2 rounded">
                                                        {existingImages.length + index + 1}
                                                    </span>
                                                </div>
                                              );
                                          })}
                                      </div>
                                  </div>
                                )}
                            </div>

                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2" htmlFor="description">
                                    Description (Optional)
                                </label>
                                <textarea
                                  id="description"
                                  value={description}
                                  onChange={handleDescriptionChange}
                                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
                                  placeholder="Enter project description"
                                  rows={4}
                                />
                            </div>

                            <div className="mb-6">
                                <label className="block text-gray-700 font-medium mb-2" htmlFor="projectType">
                                    Project Type *
                                </label>
                                <select
                                  id="projectType"
                                  value={projectType}
                                  onChange={handleProjectTypeChange}
                                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
                                >
                                    <option value="">Select a project type</option>
                                    {PROJECT_TYPES.map(function (type) {
                                        return (
                                          <option key={type} value={type}>
                                              {type.charAt(0).toUpperCase() + type.slice(1)}
                                          </option>
                                        );
                                    })}
                                </select>
                            </div>

                            {error && (
                              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                                  {error}
                              </div>
                            )}

                            {success && (
                              <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                                  {success}
                              </div>
                            )}

                            <button
                              type="submit"
                              disabled={submitting}
                              className={`w-full py-2 px-4 border-r-4 rounded font-medium text-white transition-colors
                                ${submitting
                                ? 'bg-[#0d1f79] cursor-not-allowed'
                                : 'bg-[#081542] hover:bg-[#0d1f79]'
                              }`}
                            >
                                {submitting ? 'Updating Project...' : 'Update Project'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UpdateProject;
